// Container for the lineup component with the logic layer
import React, { useState, useEffect } from 'react';
import { useGetLineupsQuery, useGetTeamPlayerStatsQuery } from '../../../../services/api/sportsApi';
import { nflTeamIds } from '../../../../utils/nflUtils';
import { mlbTeamIds } from '../../../../utils/mlbUtils';
import { nbaTeamIds } from '../../../../utils/nbaUtils';
import NflTeamLineups from './NflTeamLineups';
import MlbTeamLineups from './MlbTeamLineups';
import NbaTeamLineups from './NbaTeamLineups';
import PlayerStats from '../../../analytics/playerStats/PlayerStats';

const LineupContainer = ({sport, game, homeTeamInjuries, awayTeamInjuries, homeTeamLabel = null, awayTeamLabel = null}) => {
    // State for view switching
    const [selectedView, setSelectedView] = useState('lineup'); // 'lineup' or 'playerStats'
    const [selectedPlayerData, setSelectedPlayerData] = useState(null);

    const teamIds = 
        sport === 'nfl' ? 
        [nflTeamIds[game?.home?.team_id], nflTeamIds[game?.away?.team_id]] : 
        sport === 'mlb' ? 
        [mlbTeamIds[game?.home?.team], mlbTeamIds[game?.away?.team]] :
        sport === 'nba' ?
        [nbaTeamIds[game?.home?.team_id], nbaTeamIds[game?.away?.team_id]] :
        [];

    const globalGameID = game?.global_game_id || null;

    // Get NFL lineups
    const { data: lineups, isLoading: lineupsLoading, isError: lineupsError } = useGetLineupsQuery(
        { sport, idsArray: teamIds }, 
        { skip: !teamIds || teamIds.length === 0 || !sport || !sport === 'nfl'}
    );

    // Get MLB / NBA game lineups
    const { data: gameLineups, isLoading: gameLineupsLoading, isError: gameLineupsError } = useGetLineupsQuery(
        { sport, idsArray: [globalGameID] },
        { skip: !globalGameID || !sport || !["mlb", "nba"].includes(sport)}
    );

    const { data: teamStats, isLoading: statsLoading, isError: statsError } = useGetTeamPlayerStatsQuery(
        { sport, season: game?.season, season_type: game?.season_type, team_ids: teamIds },
        { skip: !game?.season || !game?.season_type || !teamIds || teamIds.length === 0 || !sport || !["mlb", "nba", "nfl"].includes(sport)}
    );

    // Handler for player click
    const handlePlayerClick = (player, teamID, playerType = null) => {
        const playerStats = teamStats?.[teamID]?.find(stat => stat.PlayerID === player.PlayerID);
        setSelectedPlayerData({
            player,
            stats: playerStats,
            teamID,
            playerType, // 'pitcher' or 'batter' for MLB, null for others
            teamLabel: teamID === teamIds[0] ? homeTeamLabel : awayTeamLabel
        });
        setSelectedView('playerStats');
    };

    // Handler for back navigation
    const handleBackToLineup = () => {
        setSelectedView('lineup');
        setSelectedPlayerData(null);
    };

    useEffect(() => {
       handleBackToLineup();
    }, [game]);

    // If showing player stats, render that view
    if (selectedView === 'playerStats') {
        return <PlayerStats sport={sport} playerData={selectedPlayerData} onBack={handleBackToLineup} />;
    }

    // Loading state
    if ( (sport === 'nfl' && lineupsLoading) || (["mlb", "nba"].includes(sport) && gameLineupsLoading) || statsLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" 
                         role="status">
                        <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                            Loading...
                        </span>
                    </div>
                    <p className="mt-4 text-gray-600">Loading lineup data...</p>
                </div>
            </div>
        );
    }

    // Error state
    if ( (sport === 'nfl' && lineupsError) || (["mlb", "nba"].includes(sport) && gameLineupsError) || statsError) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center text-red-600">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="mt-4 font-semibold">Error loading lineup data</p>
                    <p className="mt-2 text-sm text-gray-500">Please try again later</p>
                </div>
            </div>
        );
    }

    return sport === "nfl" ? 
        <NflTeamLineups 
            game={game} 
            teamDepthCharts={lineups}   
            teamStats={teamStats} 
            homeTeamID={teamIds[0]} 
            awayTeamID={teamIds[1]} 
            homeTeamLabel={homeTeamLabel} 
            awayTeamLabel={awayTeamLabel} 
            homeTeamInjuries={homeTeamInjuries}
            awayTeamInjuries={awayTeamInjuries}
            onPlayerClick={handlePlayerClick}
        />
        : sport === "mlb" ?
        <MlbTeamLineups 
            game={game} 
            gameLineups={gameLineups[0]}
            teamStats={teamStats} 
            homeTeamID={teamIds[0]} 
            awayTeamID={teamIds[1]} 
            homeTeamLabel={homeTeamLabel} 
            awayTeamLabel={awayTeamLabel}
            onPlayerClick={handlePlayerClick}
        />
        : sport === "nba" ?
        <NbaTeamLineups 
            gameLineups={gameLineups[0]}
            teamStats={teamStats} 
            homeTeamID={teamIds[0]} 
            awayTeamID={teamIds[1]} 
            homeTeamLabel={homeTeamLabel} 
            awayTeamLabel={awayTeamLabel}
            onPlayerClick={handlePlayerClick}
        />
        : null;
    ;
};

export default LineupContainer;